/**
 * BackOffice Type Overrides
 *
 * This file is for overriding/correcting the swagger-generated types. When overriding a type:
 * 1. Import the incorrect type without "Dto" so that the original name can be used for export
 * 2. Add a jsdoc-style comment with the name of the original type (just for file readability)
 * 3. Create new overridden type and export it using the original name from data-contracts
 * 4. Explicitly re-export the new type from index.ts in this folder (necessary to avoid ambiguity)
 */

import { Injectable } from '@jack-henry/frontend-utils/di';
import { TmHttpClient } from '@treasury/core/http';
import { createSwaggerClient } from '../utils/tm-swagger-client';
import {
    AchCompaniesClient as GeneratedAchCompaniesClient,
    AuthenticationClient as GeneratedAuthenticationClient,
    CompaniesClient as GeneratedCompaniesClient,
    CompanyuserClient as GeneratedCompanyuserClient,
} from './generated/clients';
import {
    AccountModelDto,
    AccountProductConfigurationModelDto,
    AchCompanyModelDto,
    CompanyAccountModelDto,
    EntitlementGroupModelDto,
    FiProductFeatureModelDto,
    FiPermissionModelDto as GeneratedFiPermissionModelDto,
    InformationReportingProductFeatureModelDto as GeneratedInformationReportingProductFeatureModelDto,
    WireCompanyAccountModelDto as GeneratedWireCompanyAccountModelDto,
    WireCompanyModelDto as GeneratedWireCompanyModelDto,
    ProductFeatureModelDto,
    RemoteDepositProductModelDto,
    RoleModelDto,
    SearchCompanyUserModelDto,
} from './generated/data-contracts';
import { ContentType, HttpClient, RequestParams } from './generated/http-client';

/**
 * ProductFeatureModelDto
 */

export interface FromAccountPermissionDto {
    id: number;
    fromAccountType: string;
    isAllowed: boolean;
}
export interface AccountPermissionDto {
    id: number;
    toAccountType: string;
    fromAccountTypes: FromAccountPermissionDto[];
}
export interface TransferProductFeatureModelDto extends ProductFeatureModelDto {
    accounts?: AccountModelDto[];
    accountPermissions: AccountPermissionDto[];
    allowLoanPaymentPastDue?: boolean;
    approval?: 'None' | number;
    dailyLimit?: number;
    templateApproval?: 'None' | number;
}

export interface FiTransferProductFeatureModelDto extends FiProductFeatureModelDto {
    accounts?: AccountModelDto[];
    accountPermissions: AccountPermissionDto[];
    allowLoanPaymentPastDue?: boolean;
    excludeLoanPaymentLimitValidation?: boolean;
    approval?: 'None' | number;
    dailyLimit?: number;
    templateApproval?: 'None' | number;
}

export interface FiThresholdApprovalModelDto {
    id?: number;
    startAmount: number;
    endAmount?: number;
    approvalCount: number;
}

export interface FiWireProductFeatureModelDto extends FiProductFeatureModelDto {
    isInternational?: boolean;
    showInternationalUsd?: boolean;
    wireDliInternationalEnabled?: boolean;
    requirePurpose?: boolean;
    limitFutureDateEnabled?: boolean;
    maximumFutureDays?: number;
    allowWireFileUpload?: boolean;
    paymentApproval?: string;
    beneficiaryApproval?: string;
    templateApproval?: string;
    paymentApprovalType?: string;
    paymentThresholdApprovals?: FiThresholdApprovalModelDto[];
}

export interface FiWireDliProductFeatureModelDto extends FiProductFeatureModelDto {
    requirePurpose?: boolean;
    useRatesheets?: boolean;
    fxClientId?: string;
    wireSettlementAccount?: string;
    outgoingSettlementTypeId?: number;
    allowAccountBalanceValidation?: boolean;
    validateAccountBalance?: boolean;
    paymentApproval?: string;
    beneficiaryApproval?: string;
    paymentApprovalType?: string;
    paymentThresholdApprovals?: FiThresholdApprovalModelDto[];
    fxPaymentNotificationEmailAddresses?: string[];
}

export interface FiOnUsRoutingNumberModelDto {
    routingNumber: string;
    bankName: string;
}

export interface FiSecCodeModelDto {
    code: string;
    description?: string;
}

export interface FiAchProductFeatureModelDto extends FiProductFeatureModelDto {
    daysInAdvance?: number;
    allowSameDayAch?: boolean;
    sameDayAchLimit?: number;
    accountBalanceValidationConfiguredInCore?: boolean;
    validateAccountBalance?: boolean;
    allowCopyInitiatedNachaFiles?: boolean;
    copyInitiatedNachaFiles?: boolean;
    allowChildSupportPayments?: boolean;
    allowUnbalancedPayments?: boolean;
    allowOnUsAccessManagement?: boolean;
    allowInternationalAchPayments?: boolean;
    allowAchNotificationOfChange?: boolean;
    allowReturnReview?: boolean;
    secCodes?: FiSecCodeModelDto[];
    paymentApprovalCount?: number;
    batchApprovalCount?: number;
    internationalTemplateApprovalCount?: number;
    recipientApprovalCount?: number;
    achCompanyMatchType?: number;
    allowTaxPayments?: boolean;
    allowFederalTaxPayments?: boolean;
    allowStateTaxPayments?: boolean;
    onUsRoutingNumbers?: FiOnUsRoutingNumberModelDto[];
}

export interface FiElectronicDocumentsProductFeatureModelDto extends FiProductFeatureModelDto {
    documentType?: number;
    electronicDocumentServiceName?: string;
    electronicDocumentServiceUri?: string;
    showSsoFields?: boolean;
    ssoClientId?: string;
    ssoSharedKey?: string;
    monthlyFee?: number;
}

export interface FiWausauStatementsProductFeatureModelDto extends FiProductFeatureModelDto {
    productDisplayName?: string;
    errorMessage?: string;
    noStatementMessage?: string;
    selectAccountLabel?: string;
}

export interface FiExportFormatModelDto {
    name: string;
    isConfigured: boolean;
}

export interface FiReportParameterModelDto {
    reportParameterName?: string;
    hasDownload?: boolean;
    hasCsv?: boolean;
    hasPdf?: boolean;
    hasBai?: boolean;
    supportsBai?: boolean;
    [key: string]: unknown;
}

export interface FiReportingProductFeatureModelDto extends FiProductFeatureModelDto {
    exportFormats?: FiExportFormatModelDto[];
    reportParameters?: FiReportParameterModelDto[];
}

export interface WireCompanyAccountModelDto extends GeneratedWireCompanyAccountModelDto {
    isEnabled?: boolean;
    isDisabled?: boolean;
}

export interface WireCompanyModelDto extends GeneratedWireCompanyModelDto {
    wireCompanyAccounts?: WireCompanyAccountModelDto[];
}

export interface InformationReportingProductFeatureModelDto extends GeneratedInformationReportingProductFeatureModelDto {
    enableIntuitFormattedExports: boolean;
}

export interface StopPaymentProductFeatureModelDto extends ProductFeatureModelDto {
    accounts?: AccountModelDto[];
    approvalCount: number;
}

export interface AccountReconProductFeatureModelDto extends ProductFeatureModelDto {
    accounts?: AccountProductConfigurationModelDto[];
    companyId: number;
}

export interface AchOffsetAccountsModelDto {
    nonManagedAccounts: CompanyAccountModelDto[];
    managedAccounts: CompanyAccountModelDto[];
}

/**
 * Overrides the generated CompanyUserSearch method to use the correct type
 *
 * @tags CompanyUser
 * @name CompanyUserSearch
 * @summary Searches for users.
 * @request POST:/companyuser/{companyId}/users/search
 */
@Injectable()
export class CompanyUserClient extends GeneratedCompanyuserClient {
    companyUserSearch2 = (
        searchUserModel: SearchCompanyUserModelDto | null,
        params: RequestParams = {}
    ) =>
        // @ts-expect-error
        this.http.request<UserSummaryModelDto[], any>({
            path: `/companyuser/${searchUserModel!.companyId}/users/search`,
            method: 'POST',
            body: searchUserModel,
            type: ContentType.Json,
            format: 'json',
            ...params,
        });
}

@Injectable()
export class CompaniesClient extends GeneratedCompaniesClient {
    // @ts-expect-error
    companiesEditRemoteDepositProduct = (
        companyId: number,
        product: RemoteDepositProductModelDto | null,
        params: RequestParams = {}
    ) =>
        // @ts-expect-error
        this.http.request<RemoteDepositProductModelDto, any>({
            path: `/Companies/${companyId}/EditRemoteDepositProduct`,
            method: 'PUT',
            body: product,
            type: ContentType.Json,
            format: 'json',
            ...params,
        });
}

@Injectable()
export class AchCompaniesClient extends GeneratedAchCompaniesClient {
    achCompaniesSearchAll = (
        companyId: number,
        query: {
            /** Contains search on Company Name */
            companyName: string | null;
        },
        params: RequestParams = {}
    ) =>
        // @ts-expect-error
        this.http.request<AchCompanyModelDto[], any>({
            path: `/achCompanies/${companyId}/search`,
            method: 'GET',
            query: query,
            format: 'json',
            ...params,
        });
}

@Injectable()
export class RolesClient {
    private readonly http: HttpClient;

    constructor(tmHttp: TmHttpClient) {
        this.http = createSwaggerClient(tmHttp, HttpClient);
    }

    /**
     * No description
     *
     * @tags Roles
     * @name RolesGetAll
     * @summary Returns the roles.
     * @request GET:/api/Roles
     */
    rolesGetAll = (params: RequestParams = {}) =>
        this.http.request<RoleModelDto[], any>({
            path: `/Roles`,
            method: 'GET',
            format: 'json',
            ...params,
        });

    rolesGetById = (roleId: number, params: RequestParams = {}) =>
        this.http.request<RoleModelDto, any>({
            path: `/Roles/${roleId}`,
            method: 'GET',
            format: 'json',
            ...params,
        });

    rolesAddRole = (role: RoleModelDto, params: RequestParams = {}) =>
        this.http.request<void, RoleModelDto>({
            path: `/Roles`,
            method: 'POST',
            body: { ...role, id: -1 },
            type: ContentType.Json,
            ...params,
        });

    rolesUpdateRole = (role: RoleModelDto, params: RequestParams = {}) =>
        this.http.request<void, RoleModelDto>({
            path: `/roles/${role.id}`,
            method: 'PUT',
            body: role,
            ...params,
        });

    rolesDeleteRole = (roleId: number, params: RequestParams = {}) =>
        this.http.request<void, any>({
            path: `/Roles/${roleId}`,
            method: 'DELETE',
            ...params,
        });
}

@Injectable()
export class EntitlementGroupsClient {
    private readonly http: HttpClient;

    constructor(tmHttp: TmHttpClient) {
        this.http = createSwaggerClient(tmHttp, HttpClient);
    }

    /**
     * No description
     *
     * @tags EntitlementGroups
     * @name EntitlementGroupsGet
     * @summary Returns the permissions for the logged in user.
     * @request GET:/api/EntitlementGroups
     */
    entitlementGroupsGet = (params: RequestParams = {}) =>
        this.http.request<EntitlementGroupModelDto[], any>({
            path: `/EntitlementGroups`,
            method: 'GET',
            format: 'json',
            ...params,
        });
}
export interface ValidateResponseDto {
    responseDetailCollection: unknown;
    success: boolean;
    token: string;
}

export interface UserDto {
    alias: string;
    financialInstitutionId: string;
    institutionUniversalId: string;
    userUniqueId: string;
}

@Injectable()
export class PlatformAuthenticationClient {
    private readonly http: HttpClient;

    constructor(tmHttp: TmHttpClient) {
        this.http = createSwaggerClient(tmHttp, HttpClient);
    }

    validate = (institutionId: string | null, params: RequestParams = {}) =>
        this.http.request<ValidateResponseDto, any>({
            path: `/institutions/${institutionId}/authentication/validate`,
            method: 'POST',
            format: 'json',
            ...params,
        });

    getUser = (institutionId: string, params: RequestParams = {}) =>
        this.http.request<UserDto, any>({
            path: `/institutions/${institutionId}/authentication/currentUser`,
            method: 'GET',
            format: 'json',
            ...params,
        });
}

export type FiPermissionModelDto = Omit<GeneratedFiPermissionModelDto, 'claimPrequisite'> & {
    claimPrerequisite?: string[];
};
